import graphql from "babel-plugin-relay/macro";

import {
  WhooshMutationError,
  useMutationWithError,
} from "../../../utils/hooks/useMutationWithError";
import {
  SetUserNotificationPreferencesInput,
  useSetUserNotificationPreferencesMutation,
} from "./__generated__/useSetUserNotificationPreferencesMutation.graphql";

export const useSetUserNotificationPreferences = (
  callback: () => void,
  onError?: (errors: WhooshMutationError[]) => void
) => {
  const [setUserNotificationPreferences, isUpdating] =
    useMutationWithError<useSetUserNotificationPreferencesMutation>(
      UseSetUserNotificationPreferencesMutationString
    );

  const handleUserNotificationsPreferences = (
    input: SetUserNotificationPreferencesInput
  ) => {
    setUserNotificationPreferences({
      variables: { input },
      onCompleted: () => {
        callback();
      },
      onCompletedErrorCustom: (errors: WhooshMutationError[]) => {
        onError?.(errors);
        return errors;
      },
      optimisticUpdater: (store) => {
        const userRecord = store.get(input.userId);
        if (!userRecord) return;

        const toMutableArray = (
          value?: ReadonlyArray<string | null | undefined>
        ) => Array.from(value ?? []);

        const {
          bookingReminders,
          bookingUpdates,
          eventReminders,
          eventUpdates,
          requestedTimeAvailable,
        } = input.preferences;

        const bookingRemindersArray = toMutableArray(bookingReminders);
        const bookingUpdatesArray = toMutableArray(bookingUpdates);
        const eventRemindersArray = toMutableArray(eventReminders);
        const eventUpdatesArray = toMutableArray(eventUpdates);
        const requestedTimeAvailableArray = toMutableArray(
          requestedTimeAvailable
        );

        let preferencesRecord = userRecord.getLinkedRecord(
          "notificationPreferencesChannels"
        );

        if (!preferencesRecord) {
          preferencesRecord = store.create(
            `${userRecord.getDataID()}:notificationPreferencesChannels`,
            "NotificationPreferencesChannels"
          );
          userRecord.setLinkedRecord(
            preferencesRecord,
            "notificationPreferencesChannels"
          );
        }

        preferencesRecord.setValue(bookingRemindersArray, "bookingReminders");
        preferencesRecord.setValue(bookingUpdatesArray, "bookingUpdates");
        preferencesRecord.setValue(eventRemindersArray, "eventReminders");
        preferencesRecord.setValue(eventUpdatesArray, "eventUpdates");
        preferencesRecord.setValue(
          requestedTimeAvailableArray,
          "requestedTimeAvailable"
        );
      },
    });
  };
  return [handleUserNotificationsPreferences, isUpdating] as const;
};

export const UseSetUserNotificationPreferencesMutationString = graphql`
  mutation useSetUserNotificationPreferencesMutation(
    $input: SetUserNotificationPreferencesInput!
  ) {
    responsePayload: setUserNotificationPreferences(input: $input) {
      success
      errors {
        code

        message
        field
      }
    }
  }
`;
